import { useEffect, useRef, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Save, Sparkles, Plus, Trash2, Upload, FileText, Download, Loader2, Image as ImageIcon, Eye,
} from "lucide-react";

const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp"];
const PREVIEW_EXT = [...IMG_EXT, "pdf"];

export default function SectionView({ projectId, section }) {
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const hasTable = Array.isArray(section.columns);

  const token = localStorage.getItem("ordine3_token");

  const load = () => {
    setLoading(true);
    api.get(`/projects/${projectId}/sections/${section.key}`)
      .then(({ data }) => {
        setNotes(data.notes || "");
        setRows(data.rows || []);
        setFiles(data.files || []);
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [projectId, section.key]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/sections/${section.key}`, { notes, rows });
      toast.success("Salvato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runAI = async () => {
    setAiLoading(true);
    const call = () => api.post(`/projects/${projectId}/sections/${section.key}/ai`,
      { prompt: notes ? `Tieni conto di queste note esistenti: ${notes}` : "" },
      { timeout: 90000 });
    try {
      let data;
      try {
        ({ data } = await call());
      } catch (e1) {
        // 1x retry: the LLM gateway occasionally returns a transient 502
        ({ data } = await call());
      }
      const newNotes = (notes ? notes + "\n\n" : "") + data.text;
      setNotes(newNotes);
      await api.put(`/projects/${projectId}/sections/${section.key}`, { notes: newNotes, rows });
      toast.success("Testo generato e salvato");
    } catch (err) {
      toast.error("Generazione AI non riuscita, riprova tra poco");
    } finally {
      setAiLoading(false);
    }
  };

  const addRow = () => {
    const empty = {};
    section.columns.forEach((c) => (empty[c.key] = ""));
    setRows([...rows, empty]);
  };
  const updateCell = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    setRows(next);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/sections/${section.key}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles([data, ...files]);
      toast.success("File caricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const deleteFile = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      setFiles(files.filter((f) => f.id !== id));
      toast.success("File rimosso");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fileUrl = (f) => `${API}/files/${f.id}/download?auth=${token}`;
  const ext = (f) => (f.original_filename.split(".").pop() || "").toLowerCase();
  const isImage = (f) => IMG_EXT.includes(ext(f));
  const canPreview = (f) => PREVIEW_EXT.includes(ext(f));
  const [preview, setPreview] = useState(null);

  if (loading) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-10 fade-up">
      <div>
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Sezione</p>
        <h2 className="font-heading text-3xl font-light tracking-tight mt-2">{section.label}</h2>
        <p className="text-sm text-muted-foreground mt-1">{section.hint}</p>
      </div>

      {/* Notes */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Note</span>
          {section.ai && (
            <Button data-testid="ai-generate-button" variant="outline" size="sm" onClick={runAI} disabled={aiLoading}
              className="rounded-none gap-2">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {section.aiLabel || "Genera con AI"}
            </Button>
          )}
        </div>
        <Textarea data-testid="section-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={hasTable ? 4 : 10} placeholder="Scrivi qui note, descrizioni, indicazioni..."
          className="rounded-none focus-visible:ring-foreground resize-y" />
      </div>

      {/* Table */}
      {hasTable && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Tabella</span>
            <Button data-testid="add-row-button" variant="outline" size="sm" onClick={addRow} className="rounded-none gap-2">
              <Plus className="h-4 w-4" /> Aggiungi riga
            </Button>
          </div>
          <div className="border border-border bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {section.columns.map((c) => (
                    <TableHead key={c.key} className="text-xs uppercase tracking-[0.08em] whitespace-nowrap">{c.label}</TableHead>
                  ))}
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={section.columns.length + 1} className="text-center text-sm text-muted-foreground py-8">
                      Nessuna riga. Clicca "Aggiungi riga".
                    </TableCell>
                  </TableRow>
                ) : rows.map((row, i) => (
                  <TableRow key={i}>
                    {section.columns.map((c) => (
                      <TableCell key={c.key} className="p-1">
                        <Input value={row[c.key] || ""} onChange={(e) => updateCell(i, c.key, e.target.value)}
                          className="rounded-none border-transparent hover:border-border focus-visible:ring-foreground h-9 min-w-[120px]" />
                      </TableCell>
                    ))}
                    <TableCell className="p-1">
                      <Button variant="ghost" size="icon" onClick={() => removeRow(i)}
                        className="rounded-none h-8 w-8 text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Button data-testid="save-section-button" onClick={save} disabled={saving}
        className="rounded-none gap-2 bg-foreground text-background hover:bg-foreground/90">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva sezione
      </Button>

      {/* Files */}
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Documenti allegati</span>
          <input ref={fileInput} type="file" className="hidden" onChange={onUpload} data-testid="file-input" />
          <Button data-testid="upload-document-button" variant="outline" size="sm" disabled={uploading}
            onClick={() => fileInput.current?.click()} className="rounded-none gap-2">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica file
          </Button>
        </div>

        {files.length === 0 ? (
          <div className="border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
            Nessun documento caricato in questa sezione.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {files.map((f) => (
              <div key={f.id} data-testid={`file-${f.id}`} className="bg-white p-4 flex flex-col gap-3">
                <button
                  onClick={() => canPreview(f) ? setPreview(f) : window.open(fileUrl(f), "_blank")}
                  data-testid={`preview-${f.id}`}
                  className="block aspect-video bg-secondary overflow-hidden group/thumb relative"
                >
                  {isImage(f) ? (
                    <img src={fileUrl(f)} alt={f.original_filename} className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full grid place-items-center">
                      <FileText className="h-8 w-8 text-muted-foreground" strokeWidth={1} />
                    </div>
                  )}
                  {canPreview(f) && (
                    <span className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/40 transition-colors grid place-items-center opacity-0 group-hover/thumb:opacity-100">
                      <Eye className="h-6 w-6 text-white" />
                    </span>
                  )}
                </button>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" title={f.original_filename}>{f.original_filename}</p>
                    <p className="text-xs text-muted-foreground uppercase">{ext(f)} · {(f.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {canPreview(f) && (
                      <Button variant="ghost" size="icon" onClick={() => setPreview(f)} data-testid={`preview-btn-${f.id}`}
                        className="rounded-none h-8 w-8"><Eye className="h-4 w-4" /></Button>
                    )}
                    <a href={fileUrl(f)} target="_blank" rel="noreferrer" download>
                      <Button variant="ghost" size="icon" className="rounded-none h-8 w-8"><Download className="h-4 w-4" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => deleteFile(f.id)}
                      className="rounded-none h-8 w-8 text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="rounded-none max-w-4xl w-[92vw]">
          <DialogHeader>
            <DialogTitle className="font-heading font-light text-xl truncate pr-6">{preview?.original_filename}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="bg-secondary max-h-[75vh] overflow-auto">
              {isImage(preview) ? (
                <img src={fileUrl(preview)} alt={preview.original_filename} className="w-full h-auto object-contain" />
              ) : (
                <iframe title="preview" src={fileUrl(preview)} className="w-full h-[75vh] bg-white" />
              )}
            </div>
          )}
          <a href={preview ? fileUrl(preview) : "#"} target="_blank" rel="noreferrer" download className="inline-flex">
            <Button variant="outline" className="rounded-none gap-2"><Download className="h-4 w-4" /> Scarica</Button>
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
